import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { UserSession } from '../../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  // ===== SERVICE STATE MANAGEMENT =====

  private _user = signal<UserSession>(new UserSession());

  // Public readonly signal for components
  readonly user = this._user.asReadonly();

  // Computed signal for authentication status
  readonly isAuthenticated = computed(() => {
    const user = this._user();
    return user && user.access_token && user.access_token !== undefined;
  });

  // Computed signal for user ID
  readonly userId = computed(() => this._user()?.user?.id);

  // Computed signal for user email
  readonly userEmail = computed(() => this._user()?.user?.email);

  // ===== SERVICE SELF-INITIALIZATION =====
  constructor() {
    // Auto-initialize when service is first injected
    this.initializeService();
  }

  // ===== SERVICE INITIALIZATION =====
  private async initializeService(): Promise<void> {

    try {
      // Load user data from localStorage if available
      this.loadUserFromLocalStorage();
    } catch (error) {
    }
  }

  // ===== LOCAL STORAGE INTEGRATION =====
  private loadUserFromLocalStorage(): void {
    // Only access localStorage in browser environment
    if (!this.isBrowser) {
      return;
    }

    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        this._user.set(userData);
      }
    } catch (error) {
      // Keep default empty user session
    }
  }

  // ===== UTILITY METHODS =====
  resetService(): void {
    this.clearUser();
  }

  getServiceStatus(): {
    isHealthy: boolean;
  } {
    return {
      isHealthy: true
    };
  }

  // Setter for user data
  set appUser(user: UserSession) {
    this._user.set(user);
  }

  // Getter for user data (synchronous access)
  get appUser(): UserSession {
    return this._user();
  }

  // Clear user data (logout)
  clearUser(): void {
    this._user.set(new UserSession());
  }

  // Update specific user properties
  updateUser(updates: Partial<UserSession>): void {
    const currentUser = this._user();
    this._user.set({ ...currentUser, ...updates } as UserSession);
  }

  // Refresh user data from localStorage
  refreshUserFromLocalStorage(): void {
    // Only access localStorage in browser environment
    if (this.isBrowser) {
      this.loadUserFromLocalStorage();
    }
  }
}
